/* Created by Interactive 63 - https://interactive63.com */
/**
 * Minimal DOM construction helpers used by every editor component in place of a UI framework.
 */

/** A value that can be appended as a child: text, an element, or nothing. */
export type HChild = string | number | Node | null | undefined | false;

/** Properties accepted by `h` and `svgEl`. */
export interface HProps {
  class?: string | string[];
  style?: Partial<CSSStyleDeclaration> | string;
  dataset?: Record<string, string>;
  [key: string]: unknown;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Appends children, skipping empty values and converting text and numbers to text nodes. */
function appendChildren(parent: Element, children: HChild[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.append(typeof child === 'object' ? child : String(child));
  }
}

/** Converts a property value to attribute text; non-primitive values are ignored by callers. */
function toAttribute(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

/** Applies the shared property conventions to an element of either namespace. */
function applyProps(el: Element, props: HProps | null | undefined): void {
  if (!props) return;
  for (const [key, value] of Object.entries(props)) {
    if (value === null || value === undefined || value === false) continue;
    if (key === 'class') {
      el.setAttribute('class', Array.isArray(value) ? value.join(' ') : toAttribute(value));
    } else if (key === 'style') {
      if (typeof value === 'string') el.setAttribute('style', value);
      else Object.assign((el as HTMLElement).style, value);
    } else if (key === 'dataset') {
      Object.assign((el as HTMLElement).dataset, value);
    } else if (key.startsWith('on') && typeof value === 'function') {
      el.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else {
      el.setAttribute(key, value === true ? '' : toAttribute(value));
    }
  }
}

/**
 * Creates an HTML element with properties, attributes, event listeners and children.
 * Keys starting with "on" become listeners, "class" and "style" are handled specially,
 * "dataset" assigns data attributes, everything else is set as an attribute.
 */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: HProps | null,
  ...children: HChild[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  applyProps(el, props);
  appendChildren(el, children);
  return el;
}

/** Creates an SVG element in the SVG namespace, for overlays drawn above the stage. */
export function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs?: HProps | null,
  ...children: HChild[]
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  applyProps(el, attrs);
  appendChildren(el, children);
  return el;
}

/** Removes every child of an element. */
export function clear(el: Element): void {
  el.replaceChildren();
}
